import { useNavigate, useParams } from "react-router-dom";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import AddShoppingSubCategory from "../../Modals/addShoppingSubCategory";
import DeleteConfirmModal from "../../Modals/deleteConfirmModal";
import { showError, showToast } from "../../constants/toasts";
import {
  useLazyGetSubCategoryQuery,
  useDeleteSubCategoryMutation,
} from "../../services/shopingMeal";
import { isValidInput } from "../../utils/validations";

const SubCategoriesShopping = () => {
  const navigate = useNavigate();
  const params = useParams();
  const categoryId = params?.id as string;

  const [rows, setRows] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(totalCount / 10);
  const [getSubCategories] = useLazyGetSubCategoryQuery();
  const [deleteSubCategory] = useDeleteSubCategoryMutation();

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const loadSubCategories = async () => {
    if (!categoryId) return;
    setIsLoading(true);
    try {
      const response = await getSubCategories({
        id:
          typeof categoryId === "string"
            ? parseInt(categoryId, 10)
            : (categoryId as unknown as number),
        length: 10,
        start: page,
        search: debouncedSearchTerm,
      }).unwrap();
      if (response?.status === 200) {
        setRows(response?.data?.response_object || []);
        setTotalCount(response?.data?.total_records || 0);
      }
    } catch (e: any) {
      showError(e?.data?.message || "Failed to load sub-categories");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSubCategories();
  }, [debouncedSearchTerm, page, categoryId]);

  const handleCloseModal = () => {
    setOpen(false);
  };

  return (
    <>
      <>
        <div className="main_title">
          <h1>Sub Categories</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/shopping-meals")}>
              Shopping & Meal AI
            </span>{" "}
            - Sub Categories
          </p>
        </div>

        <div className="cards">
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  placeholder="Search..."
                  searchTerm={searchTerm}
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setIsEdit(false);
                  setSelectedId(null);
                  setOpen(true);
                }}
              >
                <AddIcon /> Add{" "}
              </Button>
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Sub Category</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow hover key={row.id}>
                    <TableCell>
                      <Typography fontWeight="bold">{row.name}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.expiry_date || row.expiry_time || row.expiry || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <Box>
                          <IconButton
                            onClick={() => {
                              setSelectedId(row.id);
                              setIsEdit(true);
                              setOpen(true);
                            }}
                          >
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Edit"
                            />
                          </IconButton>
                        </Box>
                        <Box>
                          <IconButton
                            onClick={() => {
                              setSelectedId(row.id);
                              setDeleteOpen(true);
                            }}
                          >
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Delete"
                            />
                          </IconButton>
                        </Box>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </>
      <AddShoppingSubCategory
        open={open}
        onClose={handleCloseModal}
        setOpen={setOpen}
        categoryId={categoryId}
        editId={selectedId ?? undefined}
        isEdit={isEdit}
        onSuccess={loadSubCategories}
      />
      <DeleteConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        handleDelete={async () => {
          if (!selectedId) return;
          try {
            await deleteSubCategory({ id: selectedId }).unwrap();
            showToast("Sub-category deleted successfully");
            loadSubCategories();
          } catch (e: any) {
            showError(e?.data?.message || "Failed to delete sub-category");
          }
          setDeleteOpen(false);
          setSelectedId(null);
        }}
        title="Are you sure you want to delete this sub-category?"
        message="This action cannot be undone."
      />
    </>
  );
};

export default SubCategoriesShopping;
